// Building an invoice from a slice of the family ledger.
//
// An invoice is a *snapshot*, not a live view. Once issued it must keep showing
// the numbers it showed when it was sent, even if someone later back-dates a
// payment or corrects an attendance record. So this module produces a plain
// data structure that gets frozen into the invoice document; it is never
// re-derived for display afterwards.
//
// Generating an invoice folds across an unbounded range of entries and
// allocates a sequential number — a read-modify-write a disconnected client
// cannot do correctly. MMS treats generation as asynchronous (the row appears
// with a "Creating" badge and fills in later, SPEC.md §3.4). Clients append an
// invoice *request*; a Cloud Function with a consistent view runs this function
// and writes the result. Offline, the request sits in the write queue and fires
// on reconnect, and the "Creating" badge describes its state.

import { coreSync } from './core.js';
import { isActive } from './ledger.js';
import { documentFromRows } from './documents.js';

// The three styles, read at module scope by functions/invoicing/request.js, so
// a literal — pinned to `core/src/invoicing.rs`'s by
// test/domain/familyMoneyCore.test.js.
export const DISPLAY_STYLES = {
  condensed: {
    id: 'condensed',
    label: 'Condensed',
    help: 'Combine identical items into a single line',
  },
  normal: {
    id: 'normal',
    label: 'Normal',
    help: 'Show each item on its own line',
  },
  expanded: {
    id: 'expanded',
    label: 'Expanded',
    help: 'Include lessons / events with no charges',
  },
};

export const DEFAULT_DISPLAY_STYLE = 'normal';

// Group identical lines for the condensed style. "Identical" means same
// description, same unit amount and same student — four $30 Tuesday lessons
// collapse to "4 x 30 min Lesson ... $120.00" but a $35 one stays on its own
// row, and so do two children's identical lessons, since each line names the
// student it is for (#633). An invoice condensed before #633 carries merged
// lines with no `studentId`; `invoiceLineDescription` names nobody on such a
// line, so it is drawn as it was issued.
function condense(lines) {
  const groups = new Map();
  for (const line of lines) {
    const key = `${line.description}|${line.amountMinor}|${line.kind}|${line.studentId || ''}`;
    const existing = groups.get(key);
    if (existing) {
      existing.quantity += 1;
      existing.amountMinor += line.amountMinor;
      // Keep the earliest date so the collapsed row sorts where the first of
      // its members would have.
      if (line.date < existing.date) existing.date = line.date;
    } else {
      groups.set(key, { ...line, quantity: 1, unitAmountMinor: line.amountMinor });
    }
  }
  return [...groups.values()];
}

// Build the invoice payload.
//
// entries      — every ledger entry for the family (the function slices it)
// periodStart / periodEnd — the covered range, YYYY-MM-DD
// includePreviousBalance  — MMS's "Include previous balance & payments from
//                           before the start date" vs "Only charges & discounts
//                           within the date range"
// zeroBalanceBehaviour    — 'skip' | 'create'
// emptyOccurrences        — lessons in range that produced no charge; surfaced
//                           only by the Expanded display style
//
// **No `currency`.** The payload carries none, as
// `docs/features/multi-currency.md` §5 says no money document does: an
// invoice's amounts are minor units of the studio's currency, derived from
// `settings/studio.locale` where they are drawn — the web's InvoiceDocument,
// and the reminder mail (`functions/delivery/facts.js`). Some stored invoices
// carry `currency: 'USD'` whatever their studio's Region; that is not a fact
// about the invoice, and nothing reads it.
export function buildInvoice({
  entries,
  familyId,
  periodStart,
  periodEnd,
  invoiceDate,
  includePreviousBalance = true,
  displayStyle = DEFAULT_DISPLAY_STYLE,
  zeroBalanceBehaviour = 'skip',
  emptyOccurrences = [],
  footerNote = '',
}) {
  const active = entries.filter(isActive);
  // The core answers with **ids**, not entries — the caller already holds the
  // documents and they carry fields the boundary does not (#214). Rejoining by
  // id is the shape `unreadableEntryIds` uses.
  const byId = new Map(active.map((e) => [e.id, e]));
  const inRange = coreSync()
    .sortedEntryIds(coreSync().entriesBetweenIds(active, periodStart, periodEnd)
      .map((id) => byId.get(id)))
    .map((id) => byId.get(id));

  // Everything strictly before the period start, folded to a single figure.
  const previousBalanceMinor = includePreviousBalance
    ? coreSync().balanceAsOf(active, priorDay(periodStart))
    : 0;

  let lines = inRange.map((e) => ({
    id: e.id,
    date: e.occurredOn,
    description: e.description || defaultDescription(e),
    kind: e.kind,
    // Stored unsigned, as the ledger stores it. Which column it is drawn in,
    // and whether it is taken off there (a discount, a refund), is
    // `invoiceLineView` under this invoice's `columnRule`.
    amountMinor: e.amountMinor,
    signedMinor: coreSync().kindSign(e.kind) * e.amountMinor,
    sourceType: e.sourceType || null,
    // Who the entry is for, or null (#633): what Condensed keeps apart. The
    // name each line is labelled with is read when the invoice is drawn —
    // `generateInvoice` freezes it into the body (documentRender.js).
    studentId: e.studentId || null,
  }));

  if (displayStyle === 'condensed') lines = condense(lines);

  if (displayStyle === 'expanded') {
    // Zero-charge lessons appear so the family can see the full teaching record,
    // including the ones they were not billed for. They carry no amount and must
    // not affect any total.
    lines = lines.concat(
      emptyOccurrences.map((o) => ({
        id: o.id,
        date: o.date,
        description: o.description || 'Lesson',
        kind: 'informational',
        amountMinor: 0,
        signedMinor: 0,
        sourceType: 'lesson',
      }))
    );
  }

  lines.sort((a, b) => (a.date === b.date ? String(a.id).localeCompare(String(b.id)) : a.date < b.date ? -1 : 1));

  // `Number(...) || 0` on the way in, as `ledger.js` and `lessonRemoval.js`
  // do. The core's `sum` takes a list of numbers and serde **type-checks** it
  // rather than coercing, unlike the scalar arguments either side of it — so
  // a document with a string `amountMinor`, or none at all, would throw
  // rather than fold as 0. Asserted in `test/core/money.wasm.test.js`.
  //
  // **New Charges and Payments are MMS's columns' sums (#643)**, folded by the
  // core's `invoicing::figures`: charges and packages less discounts, and
  // payments less refunds — the family ledger's *Charges & Discounts* and
  // *Payments & Refunds*. Splitting them by the balance's sign would count a
  // $10 discount as a payment and a refund as a new charge. Total Due is the
  // same sum either way. `columnRule` is written beside them so the lines are
  // drawn the way the figures were folded: an invoice without one (built
  // before #643) keeps its old placement — `invoicing::line_view`.
  const figures = coreSync().invoiceFigures(
    inRange.map((e) => String(e.kind)),
    inRange.map((e) => Number(e.amountMinor) || 0),
  );
  const newChargesMinor = figures.newChargesMinor;
  const paymentsMinor = figures.paymentsMinor;
  const totalDueMinor = previousBalanceMinor + newChargesMinor - paymentsMinor;

  // The printed invoice opens with a "(Balance Forward)" row dated to the start
  // of the period, carrying whatever was outstanding before it.
  const balanceForwardLine =
    includePreviousBalance && previousBalanceMinor !== 0
      ? {
          id: 'balance-forward',
          date: periodStart,
          description: '(Balance Forward)',
          kind: 'balance_forward',
          amountMinor: previousBalanceMinor,
          signedMinor: previousBalanceMinor,
          sourceType: null,
        }
      : null;

  return {
    familyId,
    invoiceDate,
    periodStart,
    periodEnd,
    displayStyle,
    previousBalanceMinor,
    paymentsMinor,
    newChargesMinor,
    totalDueMinor,
    columnRule: figures.columnRule,
    lines: balanceForwardLine ? [balanceForwardLine, ...lines] : lines,
    footerNote,
    // The generator consults this instead of deciding for itself, so the batch
    // runner can skip an entire family without special-casing.
    shouldIssue: !(zeroBalanceBehaviour === 'skip' && totalDueMinor <= 0),
    // Which entries this invoice covers. Recorded so a reissue can be compared
    // against the original, and so an entry can be traced to the invoice it
    // appeared on.
    entryIds: inRange.map((e) => e.id),
  };
}

function priorDay(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - 1));
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

function defaultDescription(entry) {
  if (entry.sourceType === 'lesson') return 'Lesson';
  return entry.kind.charAt(0).toUpperCase() + entry.kind.slice(1);
}

// ─── How many families one generation request may name ──────────────────────
//
// A hundred, enforced in three places that have to agree: firestore.rules
// refuses a longer `familyIds` array, functions/invoicing/request.js refuses
// one that reached the trigger anyway (a request is checked by the rules when
// it is *queued*, so an offline client's write can arrive having been checked
// against older rules), and the chunking below keeps the client under it.
//
// Generation reads the *whole* ledger for every family a request names (a
// previous balance folded from a window would be silently smaller). A hundred
// families finishes inside the trigger's timeout; a timed-out invocation
// cannot write `failed` back onto the request, so the "Creating" badge would
// never resolve.
//
// A teacher never meets it: MMS pages its family picker; ours is one list, so
// a 200-family studio selecting everybody gets two requests rather than a
// refusal — see `invoiceRequestDocuments` below and src/data/useInvoices.jsx.
//
// The splitting itself is `core/src/invoicing.rs`'s, and this literal is
// pinned to the core's by test/domain/familyMoneyCore.test.js: the function
// reads it at module scope.
export const MAX_FAMILIES_PER_INVOICE_REQUEST = 100;

// ─── What the client decides about an invoice ──────────────────────────────
//
// **Adapters.** New Invoice's form, the request documents a run writes, and
// what the Invoices tab and the invoice document show are
// `core/src/invoicing.rs`, so the phones ask the same questions of the same
// code; `core/src/family_money_vectors.json` holds the recorded answers.
// Generation is the server's (`buildInvoice` above).

const text = (v) => (v == null ? null : String(v));

/**
 * A run the rules would refuse (`validInvoiceRequest`), refused before it is
 * queued: no family, a period that is not two dates or ends before it starts,
 * an invoice date that is not one, an option that does not exist, a footer
 * past the bound. Queued, the server would refuse them after the drawer had
 * closed.
 */
export class InvoiceRequestRefused extends Error {
  constructor(refusal) {
    super(refusal.message);
    this.name = 'InvoiceRequestRefused';
    this.code = refusal.code;
  }
}

/** New Invoice's "What do you want to include?" radio, in its order. */
export const includePreviousOptions = () => coreSync().invoiceIncludeOptions();

/** New Invoice's zero-balance radio, in its order. */
export const zeroBalanceOptions = () => coreSync().invoiceZeroBalanceOptions();

/**
 * The footer note once the studio's default arrives: it only ever fills an
 * empty note, so it cannot overwrite one the teacher has typed.
 */
export const seedFooterNote = (note, defaultFooter) => (
  coreSync().seedFooterNote(text(note), text(defaultFooter)) ?? (note === undefined ? undefined : null)
);

/**
 * The New Invoice form as it opens: nobody picked, dated today, the period
 * handed in (the current month), previous balance included, Normal, skip a
 * zero balance, and an empty note for `seedFooterNote` to fill.
 */
export function newInvoiceForm({ today, periodStart, periodEnd }) {
  const f = coreSync().newInvoiceForm(today, periodStart, periodEnd);
  return {
    familyIds: [],
    invoiceDate: f.invoiceDate,
    includePreviousBalance: f.includePreviousBalance,
    periodStart: f.periodStart,
    periodEnd: f.periodEnd,
    displayStyle: f.displayStyle,
    zeroBalanceBehaviour: f.zeroBalanceBehaviour,
    footerNote: f.footerNote,
    // Step two's Email (#622) is absent until chosen: absent is MMS's
    // default, "No, create but don't email" (`requestForm` below).
  };
}

/** Whether step one's Next is live: at least one family picked. */
export const invoiceCanContinue = (form) => coreSync().invoiceCanContinue(form.familyIds.length);

// The request's settings as the core reads them: an absent one is the
// default in the destructuring below, and `null` is a stored null.
function requestForm({
  invoiceDate, includePreviousBalance = true, periodStart, periodEnd,
  displayStyle = 'normal', zeroBalanceBehaviour = 'skip', footerNote = '', emailInvoices = false,
}) {
  return {
    invoiceDate: text(invoiceDate),
    includePreviousBalance: includePreviousBalance == null ? null : includePreviousBalance === true,
    periodStart: String(periodStart ?? ''),
    periodEnd: String(periodEnd ?? ''),
    displayStyle: text(displayStyle),
    zeroBalanceBehaviour: text(zeroBalanceBehaviour),
    footerNote: text(footerNote),
    // Step two's "Yes, email invoices immediately" (#622): written onto the
    // request only when true (core/src/invoicing.rs `request_documents`).
    emailInvoices: emailInvoices === true,
  };
}

// Only strings are families; anything else in a selection is dropped.
const familyIdsOf = (form) => (form.familyIds || []).filter((id) => typeof id === 'string');

/** Why the rules would refuse this run, or null. */
export function invoiceRequestRefusal(form) {
  return coreSync().invoiceRequestRefusal(familyIdsOf(form), requestForm(form)) ?? null;
}

/**
 * The request documents one run writes, one per chunk of at most a hundred
 * families — duplicates collapsed first, in the picker's order — all in one
 * batch. The ids are the caller's. `status` is the one a client may write;
 * the function moves it on. Ask `invoiceRequestRefusal` first: these are
 * built whatever the rules would say of them.
 */
export function invoiceRequestDocuments(form, serverTimestamp) {
  const ids = familyIdsOf(form);
  const settings = requestForm(form);
  const n = coreSync().invoiceRequestCount(ids);
  return Array.from({ length: n }, (_, i) => documentFromRows(
    coreSync().invoiceRequestDocument(ids, settings, i), serverTimestamp,
  ));
}

// ── The Invoices tab ───────────────────────────────────────────────────────

/** A request the function has not finished: the "Creating" row. */
export const isPendingRequest = (r) => coreSync().invoiceRequestIsPending(r?.status);
/** A request the function gave up on: a row that stays visible. */
export const isFailedRequest = (r) => coreSync().invoiceRequestIsFailed(r?.status);

/**
 * Whether the tab shows its empty state rather than the table. A skipped
 * row is a finished run's result, drawn among the invoices, so it is
 * counted with them: a studio whose only run skipped everybody sees that
 * run rather than "You don't have any invoices yet".
 */
export const invoiceListIsEmpty = ({ invoices, pending, failed, skipped = [] }) => coreSync().invoiceListIsEmpty(
  invoices.length + skipped.length, pending.length, failed.length,
);

/**
 * The Family cell of a Creating row: the family's name when the run named
 * one, else how many. `nameOf` is the list's own lookup.
 */
export function requestFamilyText(familyIds, nameOf) {
  const count = familyIds?.length || 0;
  return coreSync().invoiceRequestFamilyText(count, count === 1 ? String(nameOf(familyIds[0])) : '');
}

/** The Date Range cell. */
export const invoicePeriodText = (start, end) => coreSync().invoicePeriodText(String(start), String(end));

/**
 * The Invoices tab's rows in MMS's order — `Invoice Date ↓`, then the higher
 * invoice number — rather than the read's document-id order. The rule is
 * `invoicing::list_order` in the core, which both phones ask too. Takes
 * invoices or requests (a request has no number, so it goes by date alone).
 */
export function invoiceListOrder(rows) {
  const positions = coreSync().invoiceListOrder(
    rows.map((r) => String(r.invoiceDate ?? '')),
    rows.map((r) => (Number.isFinite(r.invoiceNumber) ? r.invoiceNumber : null)),
  );
  return [...positions].map((i) => rows[i]);
}

/** A failed request's sentence. */
export const failedRequestText = (error) => coreSync().invoiceFailedText(text(error));

/** The Invoice Amount cell of a skipped row. */
export const skippedBadgeText = () => coreSync().invoiceSkippedBadge();

/**
 * One row per family a finished run passed over because it owed nothing
 * (#579) — the rule, and why MMS lists nothing here and we do, is
 * `invoicing::skip_is_listed` in the core. Read straight off the requests'
 * own `skipped` arrays, which the function writes, so nothing else is stored.
 *
 * A row carries what an invoice row draws (`invoiceDate`, `familyId`, the
 * period) and no `invoiceNumber`, so `invoiceListOrder` puts it after the
 * numbered invoices of its date. `key` is unique across the list: one
 * request skips a family at most once.
 */
export function skippedInvoiceRows(requests) {
  const rows = [];
  for (const r of requests || []) {
    if (!Array.isArray(r?.skipped)) continue;
    for (const skip of r.skipped) {
      const reason = typeof skip?.reason === 'string' ? skip.reason : null;
      if (typeof skip?.familyId !== 'string') continue;
      if (!coreSync().invoiceSkipIsListed(typeof r.status === 'string' ? r.status : null, reason)) continue;
      rows.push({
        key: `${r.id}__${skip.familyId}`,
        requestId: r.id,
        familyId: skip.familyId,
        invoiceDate: r.invoiceDate,
        periodStart: r.periodStart,
        periodEnd: r.periodEnd,
      });
    }
  }
  return rows;
}

// ── The invoice document ───────────────────────────────────────────────────

/**
 * The document's heading, footer and bottom line, from the snapshot and
 * Business Settings → Accounts & Invoices (`invoicing`, normalised).
 *
 * A family in credit reads `Total Due: <zero>` or, when the studio chose
 * `Balance Remaining`, the credit as a positive figure under that label.
 */
export function invoiceDocumentView(invoice, { studioName, invoicing }) {
  return coreSync().invoiceDocumentView(
    Number(invoice.totalDueMinor) || 0,
    text(invoice.footerNote),
    text(studioName),
    text(invoicing.invoiceName),
    text(invoicing.footerText),
    text(invoicing.negativeDisplay),
  );
}

/**
 * One line of the table: which column its amount sits in (neither when it
 * is zero), signed as it counts there, and the `N × ` a condensed line
 * carries. `columnRule` is the invoice's own (#643): absent on one built
 * before it, which keeps the placement its stored header was folded with.
 */
export function invoiceLineView(l, columnRule = null) {
  const rule = Number(columnRule);
  const v = coreSync().invoiceLineView(
    String(l.kind ?? ''), Math.round(Number(l.amountMinor) || 0), l.quantity == null ? null : Number(l.quantity),
    columnRule == null || !Number.isFinite(rule) ? null : rule,
  );
  return { prefix: v.prefix, chargeMinor: v.chargeMinor ?? null, paymentMinor: v.paymentMinor ?? null };
}

/**
 * A line's description as the invoice writes it, naming the student it is
 * for (#633): after a lesson (`4:00 PM (30 min.) - Lesson - Nguyen, Ava`),
 * before anything else (`Nguyen, Ben - Sibling discount`), and not at all
 * when `studentName` is empty — nor on a condensed line written before the
 * line carried its `studentId`, which may pool two students' lessons.
 * `core/src/invoicing.rs` `line_description` has what MMS was seen to do.
 */
export function invoiceLineDescription(l, studentName) {
  return coreSync().invoiceLineDescription(
    String(l?.description ?? ''),
    text(studentName),
    text(l?.sourceType),
    l?.quantity == null ? null : Number(l.quantity),
    Boolean(l) && Object.prototype.hasOwnProperty.call(l, 'studentId'),
  );
}

/**
 * One heading per student, for `Group charges together by student`, or null
 * when fewer than two students are named. `studentNames[i]` is line i's
 * student, empty for a line with none; those collect last under the family's
 * name. Groups are `{ key, heading, lines }`, `lines` being positions.
 */
export function invoiceStudentGroups(studentNames, familyName) {
  const rows = coreSync().invoiceStudentGroups(studentNames.map((n) => String(n || '')), text(familyName));
  if (!rows.length) return null;
  const groups = [];
  for (const r of rows) {
    if (!groups[r.group]) groups[r.group] = { key: r.key, heading: r.heading, lines: [] };
    groups[r.group].lines.push(r.line);
  }
  return groups;
}

/**
 * The groups of `Group charges together by student` on an invoice being
 * issued (#650), or null when fewer than two students are named: the lines
 * that name no student first, with no heading (`heading: null`) and no
 * subtotal, then each student's lines ending in their subtotal row,
 * `{ label: 'Nguyen, Ava Subtotal', minor }` — the group's Charges less its
 * Payments. `views[i]` is line i's `invoiceLineView`. What MMS was seen to
 * draw is `invoicing::document_groups` in the core.
 */
export function invoiceDocumentGroups(studentNames, views) {
  const figure = (v) => (v == null ? null : Number(v));
  const rows = coreSync().invoiceDocumentGroups(
    studentNames.map((n) => String(n || '')),
    views.map((v) => figure(v?.chargeMinor)),
    views.map((v) => figure(v?.paymentMinor)),
  );
  if (!rows.length) return null;
  const groups = [];
  for (const r of rows) {
    if (!groups[r.group]) {
      groups[r.group] = {
        heading: r.heading ?? null,
        lines: [],
        subtotal: r.subtotalLabel == null ? null : { label: r.subtotalLabel, minor: Number(r.subtotalMinor) },
      };
    }
    groups[r.group].lines.push(r.line);
  }
  return groups;
}

/**
 * Whether an invoice body writes its negatives in parentheses, `($10.00)`
 * (#649): a body that says so in `negatives` does; one without the field
 * keeps the `-$10.00` it was issued with.
 */
export const invoiceNegativesInParentheses = (negatives) => coreSync().invoiceNegativesInParentheses(text(negatives));

/** What a body being issued writes as its `negatives`: `parentheses`. */
export const invoiceNegatives = () => coreSync().invoiceNegatives();
